import { computed, createApp, markRaw, nextTick, reactive, toRefs } from 'vue';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import registerGlobals from '@/plugins/register-globals';
import router from '@/router';
import { CONTRACT_DATA_MAP, MARKET_DATA_MAP } from '@/services/notifications/proxy';
import store from '@/store';
import {
  CMS_CONTRACT_CLICK_OPEN_ORDERSLIP_REQUIRE_AUTHENTICATED,
  CMS_MARKET_RUNTIME,
} from './cms-config';
import { canTrade } from './h2h/h2h-model';

export function createCmsMarketRuntime({
  widgetStore = store,
  loadMarket,
  mock = false,
  globals = {},
} = {}) {
  const markets = reactive({});
  const pending = new Map();
  const pendingNotifications = new Map();
  const loaded = new Set();
  const latestNotifications = new Map();
  let disposed = false;
  let modal = null;
  let opening = false;
  let cancelLogin = null;
  const applyFields = (target, payload, map) => {
    for (const [from, to] of Object.entries(map)) {
      if (Object.hasOwn(payload, from)) target[to] = payload[from];
    }
  };
  const updateMarket = (market, type, payload) => {
    if (type === 'market') {
      if (market.marketId === payload.MarketId) applyFields(market, payload, MARKET_DATA_MAP);
    } else {
      const contract = market.contracts?.find((item) => item.contractId === payload.ContractId);
      if (contract) applyFields(contract, payload, CONTRACT_DATA_MAP);
    }
  };
  const runtime = {
    createScope() {
      return createCmsMarketRuntime({ widgetStore, loadMarket, mock, globals });
    },
    store: widgetStore,
    markets,
    mock,
    requireAuthentication: CMS_CONTRACT_CLICK_OPEN_ORDERSLIP_REQUIRE_AUTHENTICATED,
    globals,
    seed(items = []) {
      for (const item of Array.isArray(items) ? items : []) {
        const marketId = Number(item?.marketId);
        if (!Number.isInteger(marketId) || marketId <= 0 || loaded.has(marketId)) continue;
        const old = markets[item.marketId];
        const contracts = new Map(
          (old?.contracts || []).map((contract) => [contract.contractId, contract]),
        );
        for (const contract of Array.isArray(item.contracts) ? item.contracts : []) {
          const contractId = Number(contract?.contractId);
          if (!Number.isInteger(contractId) || contractId <= 0) continue;
          contracts.set(contractId, {
            ...contract,
            contractId: Number(contract.contractId),
            marketId: Number(item.marketId),
            marketName: contract.marketName || item.marketName,
            marketUrl: contract.marketUrl || item.marketUrl,
          });
        }
        markets[marketId] = { ...old, ...item, marketId, contracts: [...contracts.values()] };
        for (const [type, payload] of latestNotifications.values())
          updateMarket(markets[item.marketId], type, payload);
      }
    },
    async getMarket(id, { refresh = false } = {}) {
      id = Number(id);
      if (!Number.isInteger(id) || id <= 0 || disposed) throw new Error('Market unavailable');
      if (!refresh && loaded.has(id)) return markets[id];
      if (pending.has(id)) return pending.get(id);
      pendingNotifications.set(id, []);
      const request = Promise.resolve()
        .then(() =>
          loadMarket
            ? loadMarket(id, { refresh })
            : widgetStore.dispatch('market/getMarket', {
                marketId: id,
                modifyState: false,
                skipCache: refresh,
              }),
        )
        .then((market) => {
          if (disposed || !market) throw new Error('Market unavailable');
          if (markets[id]) Object.assign(markets[id], market);
          else markets[id] = market;
          // A fetched snapshot must not overwrite notifications received in flight.
          for (const [type, payload] of pendingNotifications.get(id) || [])
            updateMarket(markets[id], type, payload);
          loaded.add(id);
          return markets[id];
        })
        .finally(() => {
          pending.delete(id);
          pendingNotifications.delete(id);
        });
      pending.set(id, request);
      return request;
    },
    applyNotification(type, payload) {
      const key = `${type}:${type === 'market' ? payload.MarketId : payload.ContractId}`;
      const previous = latestNotifications.get(key)?.[1];
      latestNotifications.set(key, [type, { ...previous, ...payload }]);
      for (const queue of pendingNotifications.values()) queue.push([type, { ...payload }]);
      for (const market of Object.values(markets)) {
        updateMarket(market, type, payload);
      }
    },
    async openOrder(intent) {
      if (disposed || modal || opening || (!mock && widgetStore.state.popup.cmsOrder)) return;
      const snapshot = { ...intent };
      if (
        CMS_CONTRACT_CLICK_OPEN_ORDERSLIP_REQUIRE_AUTHENTICATED &&
        !widgetStore.getters['user/isLoggedIn'] &&
        !mock
      ) {
        cancelLogin?.();
        const stop = widgetStore.watch(
          (state, getters) => getters['user/isLoggedIn'],
          (loggedIn) => {
            if (loggedIn) {
              cancelLogin?.();
              runtime.openOrder(snapshot);
            }
          },
        );
        const stopPopup = widgetStore.watch(
          (state) => state.popup.currentPopup,
          (popup) => {
            if (!popup && !widgetStore.getters['user/isLoggedIn']) cancelLogin?.();
          },
        );
        cancelLogin = () => {
          stop();
          stopPopup();
          cancelLogin = null;
        };
        await widgetStore.dispatch('popup/setGenericPopup', {
          name: MODALS.LOGIN,
          options: { redirectAfterLogin: false },
        });
        return;
      }
      opening = true;
      try {
        const market = mock
          ? runtime.markets[Number(snapshot.marketId)]
          : await runtime.getMarket(snapshot.marketId, { refresh: true });
        if (!market) throw new Error('Market unavailable');
        const contract = market.contracts?.find((item) => item.contractId === snapshot.contractId);
        if (!runtime.canTrade(market, contract) || disposed) return;
        const OrderModal = mock
          ? (await import('./cms-order-modal/cms-order-modal.vue')).default
          : null;
        if (disposed || (!mock && widgetStore.state.popup.cmsOrder)) return;
        widgetStore.dispatch('trade/setTradeOptions', null);
        widgetStore.dispatch('trade/setTradeOptions', {
          contractId: snapshot.contractId,
          operationType: 'buy',
          operationValue: snapshot.direction,
          quantity: Math.max(
            widgetStore.getters['user/isLoggedIn'] ? 1 : 10,
            (contract.minimumClipSize || 1) - (contract.userQuantity || 0),
          ),
        });
        if (snapshot.priceCents !== null && snapshot.priceCents > 0)
          widgetStore.commit('trade/setTradePrice', snapshot.priceCents);
        if (disposed) return;
        const modalMarket = reactive({
          ...toRefs(market),
          marketShortName: computed(() => market.marketShortName || market.marketName),
        });
        if (!mock) {
          modal = { trigger: snapshot.trigger };
          widgetStore.commit(
            'popup/setCmsOrder',
            markRaw({ runtime, market: modalMarket, intent: snapshot }),
          );
          await widgetStore.dispatch('popup/setCurrentPopup', MODALS.CMS_ORDER);
          return;
        }
        const host = document.createElement('div');
        document.body.appendChild(host);
        const app = runtime.createApp(OrderModal, {
          intent: snapshot,
          runtime,
          market: modalMarket,
          embeddedJourney: true,
          onCompleted: () => runtime.closeOrder(),
          onClose: () => runtime.closeOrder(),
        });
        modal = { app, host, trigger: snapshot.trigger };
        app.mount(host);
      } catch (error) {
        runtime.closeOrder();
        if (!disposed)
          widgetStore.dispatch('notifications/addSimple', {
            title: 'Order unavailable',
            text: error.message,
            icon: 'cancel',
          });
      } finally {
        opening = false;
      }
    },
    closeOrder() {
      if (!modal) return;
      const previous = modal;
      modal = null;
      if (mock) {
        previous.app.unmount();
        previous.host.remove();
      } else if (widgetStore.state.popup.cmsOrder?.runtime === runtime) {
        widgetStore.commit('popup/setCmsOrder', null);
        if (widgetStore.state.popup.currentPopup === MODALS.CMS_ORDER)
          widgetStore.dispatch('popup/setCurrentPopup', null);
      }
      nextTick(() => {
        if (
          !widgetStore.state.popup.currentPopup &&
          !widgetStore.state.popup.currentSmallPopup &&
          previous.trigger?.isConnected
        )
          previous.trigger.focus({ preventScroll: true });
      });
    },
    createApp(component, props) {
      const app = createApp(component, props);
      app.use(widgetStore);
      app.use(router);
      registerGlobals(app);
      Object.assign(app.config.globalProperties, globals);
      app.provide(CMS_MARKET_RUNTIME, runtime);
      return app;
    },
    canTrade(market, contract) {
      return (
        !widgetStore.getters['app/hasMaintenance'] &&
        widgetStore.getters['app/isTradingEnabled'] !== false &&
        canTrade(market, contract)
      );
    },
    dispose() {
      disposed = true;
      cancelLogin?.();
      runtime.closeOrder();
      unsubscribe();
      pending.clear();
      pendingNotifications.clear();
      latestNotifications.clear();
    },
  };
  // The notification proxy already dispatches these for both Firebase and SignalR.
  // Listening here also covers CMS markets that are not the active market page.
  const unsubscribe = widgetStore.subscribeAction({
    before: ({ type, payload }) => {
      if (type === 'market/updateContractFromNotification')
        runtime.applyNotification('contract', payload);
      if (type === 'market/updateMarketFromNotification')
        runtime.applyNotification('market', payload);
    },
  });
  return runtime;
}
